/**
 * Browser-side image normalization before admin upload: downscale to a max edge
 * and re-encode to WebP. Runs so the API only ever stores reasonably-sized WebP,
 * regardless of what the user picked — including already-WebP exports that a
 * design tool saved at near-lossless quality (the 2 MB-for-1080px problem).
 *
 * Two independent safeguards:
 *   1. Dimension cap — longest edge ≤ MAX_EDGE (1200px); news layouts never
 *      need more, even at 2× retina.
 *   2. Quality re-encode — WebP q0.82, a visually near-identical sweet spot.
 *
 * Guards against making things worse: an already-small, within-cap WebP is left
 * untouched (recompression would only add artifacts); and if the re-encoded
 * result isn't actually smaller, the original is kept.
 *
 * AVIF inputs are also re-encoded (to apply the cap); the size guard keeps the
 * original when WebP can't beat it. GIF throws so callers can toast — canvas
 * captures one frame and would corrupt the animation. Server-side has its own
 * WASM path (apps/api/src/lib/webp-encode) for feed imports; independent of this.
 */

/** Longest-edge cap. News content columns top out ~800px CSS → 1200px covers
 *  2× retina with headroom; full-bleed heroes still look crisp. */
const MAX_EDGE = 1200;

/** An already-WebP file at or below this size AND within the cap is left as-is —
 *  recompressing a small lossy WebP only trades bytes for artifacts. */
const RECOMPRESS_WEBP_ABOVE = 400 * 1024;

/** WebP re-encode quality — near-identical to the source at a fraction of size. */
const DEFAULT_QUALITY = 0.82;

const CANVAS_TYPES = new Set(['image/jpeg', 'image/png', 'image/webp', 'image/avif']);

export class GifNotSupportedError extends Error {
  constructor() {
    super('GIF_NOT_SUPPORTED');
    this.name = 'GifNotSupportedError';
  }
}

/**
 * Normalize an image for upload: cap dimensions + re-encode to WebP. Returns the
 * original file untouched when it's already optimal (small in-cap WebP) or when
 * re-encoding wouldn't save bytes. Throws GifNotSupportedError on GIF.
 */
export async function convertImageToWebp(file: File, quality = DEFAULT_QUALITY): Promise<File> {
  const mime = file.type.toLowerCase();
  if (mime === 'image/gif') throw new GifNotSupportedError();
  // Unknown type — let the backend MIME validator reject it; don't touch bytes.
  if (!CANVAS_TYPES.has(mime)) return file;

  let out: Blob | null;
  try {
    out = await encodeCapped(file, quality);
  } catch {
    // Decode/encode failure (e.g. browser can't decode this AVIF) → upload the
    // original; the backend still validates MIME + size.
    return file;
  }

  // null = "keep original" signal; or the re-encode didn't actually shrink it.
  if (!out || out.size >= file.size) return file;

  const base = file.name.replace(/\.(jpe?g|png|webp|avif)$/i, '');
  return new File([out], `${base}.webp`, { type: 'image/webp' });
}

/**
 * Decode → optionally downscale to MAX_EDGE → encode WebP. Resolves `null` when
 * the input is an already-small, within-cap WebP (nothing worth doing).
 */
function encodeCapped(file: File, quality: number): Promise<Blob | null> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      try {
        const w = img.naturalWidth;
        const h = img.naturalHeight;
        const scale = Math.min(1, MAX_EDGE / Math.max(w, h));
        const needsDownscale = scale < 1;

        if (
          file.type.toLowerCase() === 'image/webp' &&
          !needsDownscale &&
          file.size < RECOMPRESS_WEBP_ABOVE
        ) {
          URL.revokeObjectURL(url);
          resolve(null);
          return;
        }

        const canvas = document.createElement('canvas');
        canvas.width = Math.round(w * scale);
        canvas.height = Math.round(h * scale);
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          URL.revokeObjectURL(url);
          reject(new Error('Canvas 2D context unavailable'));
          return;
        }
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        canvas.toBlob(
          (blob) => {
            URL.revokeObjectURL(url);
            if (blob) resolve(blob);
            else reject(new Error('canvas.toBlob returned null'));
          },
          'image/webp',
          quality,
        );
      } catch (e) {
        URL.revokeObjectURL(url);
        reject(e instanceof Error ? e : new Error(String(e)));
      }
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Image decode failed'));
    };
    img.src = url;
  });
}
